import { BrowserRouter, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { I18nProvider } from "@/i18n";
import { MetaProvider } from "@/contexts/MetaContext";
import { AuthProvider } from "@/contexts/AuthContext";
import LegacyRedirect from "@/components/LegacyRedirect";
import HeaderNav from "@/components/HeaderNav";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import InstallPrompt from "@/components/InstallPrompt";
import MatchesPage from "@/pages/MatchesPage";
import Watchlist from "@/pages/Watchlist";
import FeedbackPage from "@/pages/FeedbackPage";
import HypeExplainer from "@/pages/HypeExplainer";
import PlayerDetail from "@/pages/PlayerDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Leaderboard from "@/pages/Leaderboard";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Pricing from "@/pages/Pricing";
import CardPage from "@/pages/CardPage";
import SetIndex from "@/pages/SetIndex";
import SetDetail from "@/pages/SetDetail";
import AdminCards from "@/pages/AdminCards";
import AdminUsers from "@/pages/AdminUsers";
import AdminScheduler from "@/pages/AdminScheduler";
import AdminBilling from "@/pages/AdminBilling";
import AdminEmail from "@/pages/AdminEmail";
import AdminMedia from "@/pages/AdminMedia";
import Home from "@/pages/Home";
import WeekPage from "@/pages/WeekPage";
import AccountPage from "@/pages/AccountPage";
import DashboardPage from "@/pages/DashboardPage";
import MyCollection from "@/pages/MyCollection";
import CheckoutPage from "@/pages/CheckoutPage";
import GraduatedCohort from "@/pages/GraduatedCohort";
import LegalPage from "@/pages/LegalPage";
import NewsletterActionPage from "@/pages/NewsletterActionPage";
import { PaymentCancel, PaymentSuccess } from "@/pages/PaymentSuccess";

function App() {
  return (
    <I18nProvider>
      <MetaProvider>
      <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <ScrollToTop />
          <HeaderNav />
          <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/week" element={<WeekPage />} />
              <Route path="/week/:week" element={<WeekPage />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/matches" element={<MatchesPage />} />
              <Route path="/graduated" element={<GraduatedCohort />} />
              <Route path="/hype" element={<HypeExplainer />} />
              <Route path="/how-it-works" element={<HypeExplainer />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/players/:id" element={<PlayerDetail />} />
              <Route path="/cards" element={<SetIndex />} />
              {/* Anciennes URL / libellés du menu tapés à la main */}
              <Route path="/sets" element={<LegacyRedirect to="/cards" />} />
              <Route path="/sets/:slug" element={<LegacySetRedirect />} />
              <Route path="/archive" element={<LegacyRedirect to="/graduated" />} />
              <Route path="/cards/:slug" element={<CardsRouter />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/cards" element={<AdminCards />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/scheduler" element={<AdminScheduler />} />
              <Route path="/admin/billing" element={<AdminBilling />} />
              <Route path="/admin/email" element={<AdminEmail />} />
              <Route path="/admin/media" element={<AdminMedia />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/my-collection" element={<MyCollection />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/newsletter/confirm" element={<NewsletterActionPage action="confirm" />} />
              <Route path="/newsletter/unsubscribe" element={<NewsletterActionPage action="unsubscribe" />} />
            </Routes>
          </main>
          <Footer />
          <CookieConsent />
          <InstallPrompt />
        </div>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
      </AuthProvider>
      </MetaProvider>
    </I18nProvider>
  );
}

export default App;

function CardsRouter() {
  // /cards/<24-hex ObjectId> = single card page (kept for search results); anything else = set slug.
  const { slug } = useParams();
  return /^[0-9a-f]{24}$/i.test(slug || "") ? <CardPage /> : <SetDetail />;
}

function LegacySetRedirect() {
  const { slug } = useParams();
  return <LegacyRedirect to={`/cards/${slug}`} />;
}

function ScrollToTop() {
  // A SPA keeps the scroll offset across route changes: opening a player from far down the
  // watchlist landed mid-page on mobile, below his name. Every navigation starts at the top;
  // a hash link (#anchor) and the browser Back button keep their own behaviour.
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, search, hash]);
  return null;
}
